import { useState, useCallback, useRef, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import {
  selectAgentConversationFormResearchEnabled,
  selectAgentConversationFormCaselawDateRange,
  selectDefaultResearchJurisdictionForConversation,
} from '@/agent-v2/chat-form/store/selectors'
import { AgentConversationFormActions } from '@/agent-v2/chat-form/store/slice'
import { researchFiltersActions } from '../store/slice'
import { useStateSelection } from './useStateSelection'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { RiaTab } from '@/analytics/enums/ria.enums'
import { selectAreAllFederalTabSourcesSelected, selectResearchFiltersForConversation, selectNumberOfCurrentSourcesShownInFederalTab } from '../store/selectors'
import { formatRiaDateRange } from '@/analytics/utils/formatRiaDateRange'
import { StateSelectionProps } from '../types'
import { inferJurisdictionKind } from '@/jurisdiction-mappings/jurisdiction-maps'
import { JURISDICTION_TABS } from '../constants'
import type { ConversationResearchFilters } from '../store/slice'

type UseSourceDialogParams = {
  conversationId: string
  onOpenChange: (open: boolean) => void
  /** Used to detect dialog open/close events and capture initial state for calculating deltas (federalAdded, federalRemoved) in RiaSourcesApplyClicked analytics event */
  open: boolean
}

type UseSourceDialogReturn = {
  isResearchEnabled: boolean
  activeTab: string
  dateRange: { dateLow: string | null; dateHigh: string | null }
  showWarningBanner: boolean
  stateSelectionProps: StateSelectionProps
  handleTabChange: (tab: string) => void
  handleClearButtonClick: () => void
  handleApplyButtonClick: () => void
  handleDateRangeChange: (dateLow: string | null, dateHigh: string | null) => void
}

export function getInitialSourceDialogTab(params: {
  filters: ConversationResearchFilters | null
  federalSelectionCount: number
  jurisdictionForPrefill: string | null | undefined
}): string {
  const { filters, federalSelectionCount, jurisdictionForPrefill } = params
  if ((filters?.selectedStateNames.length ?? 0) > 0) return JURISDICTION_TABS.STATES
  if (federalSelectionCount > 0) return JURISDICTION_TABS.FEDERAL
  return inferJurisdictionKind(jurisdictionForPrefill) === 'federal' ? JURISDICTION_TABS.FEDERAL : JURISDICTION_TABS.STATES
}

/**
 * Custom hook to manage source dialog state and logic
 *
 * Handles:
 * - Active tab state
 * - Research enabled state derived from caselaw search filter
 * - Clear button logic (resets both caselaw and LRR filters)
 * - Apply button logic (closes the dialog)
 */
export function useSourceDialog({ conversationId, onOpenChange, open }: UseSourceDialogParams): UseSourceDialogReturn {
  const dispatch = useAppDispatch()
  const [activeTab, setActiveTab] = useState<string>(JURISDICTION_TABS.STATES)
  const { trackEvent } = useAnalytics()

  // Track initial state when dialog opens for delta calculation
  const initialStateRef = useRef<{ selectedStatesCount: number; federalCount: number } | null>(null)
  const prevDialogOpenRef = useRef(false)
  const userSelectedTabRef = useRef(false)

  // Get research enabled state
  const isResearchEnabled = useAppSelector((state: RootState) => selectAgentConversationFormResearchEnabled(state, { conversationId }))

  // Get date range state
  const dateRange = useAppSelector((state: RootState) => selectAgentConversationFormCaselawDateRange(state, { conversationId }))

  // Get state selection data (including warning banner state)
  const { showWarningBanner, selectedStates, handleStateToggle, setShowWarningBanner, includeUnpublishedCases, includeRelatedFederalContent } =
    useStateSelection(conversationId)

  // Get research filters to calculate counts
  const researchFilters = useAppSelector((state: RootState) => selectResearchFiltersForConversation(state, { conversationId }))
  const defaultResearchJurisdiction = useAppSelector((state: RootState) => selectDefaultResearchJurisdictionForConversation(state, { conversationId }))

  // Get number of federal selections (what the UI displays)
  const numberOfFederalSelections = useAppSelector((state: RootState) => selectNumberOfCurrentSourcesShownInFederalTab(state, { conversationId }))
  const riaAllFederalSourcesSelected = useAppSelector((state: RootState) => selectAreAllFederalTabSourcesSelected(state, { conversationId }))

  // Capture initial state when dialog opens for analytics delta calculation
  // This initial state is used in handleApplyButtonClick to calculate federalAdded/federalRemoved
  // values that are included in the RiaSourcesApplyClicked analytics event
  useEffect(() => {
    const isOpening = !prevDialogOpenRef.current && open
    const isClosing = prevDialogOpenRef.current && !open

    if (isOpening) {
      userSelectedTabRef.current = false
      initialStateRef.current = {
        selectedStatesCount: researchFilters?.selectedStateNames?.length ?? 0,
        federalCount: numberOfFederalSelections,
      }
    } else if (isClosing) {
      initialStateRef.current = null
      userSelectedTabRef.current = false
    }

    prevDialogOpenRef.current = open
  }, [open, researchFilters, numberOfFederalSelections])

  // Defaults may arrive after the dialog opens; keep the initial tab aligned until the user chooses a tab.
  useEffect(() => {
    if (!open || userSelectedTabRef.current) return

    setActiveTab(
      getInitialSourceDialogTab({
        filters: researchFilters,
        federalSelectionCount: numberOfFederalSelections,
        jurisdictionForPrefill: defaultResearchJurisdiction,
      })
    )
  }, [open, defaultResearchJurisdiction, researchFilters, numberOfFederalSelections])

  // Handle tab change
  const handleTabChange = useCallback(
    (tab: string) => {
      const fromTab = activeTab as RiaTab
      const toTab = tab as RiaTab

      // Track RiaSourcesTabChanged event
      trackEvent(AnalyticsEvent.RiaSourcesTabChanged, {
        riaFromTab: fromTab,
        riaToTab: toTab,
        conversationId,
      })

      userSelectedTabRef.current = true
      setActiveTab(tab)
    },
    [activeTab, trackEvent, conversationId]
  )

  // Handle date range change
  const handleDateRangeChange = useCallback(
    (dateLow: string | null, dateHigh: string | null) => {
      dispatch(
        AgentConversationFormActions.setCaselawDateRange({
          conversationId,
          dateLow,
          dateHigh,
        })
      )
    },
    [conversationId, dispatch]
  )

  // Handle clear button click - clears all research filters
  const handleClearButtonClick = useCallback(() => {
    const clearedStatesCount = researchFilters?.selectedStateNames?.length ?? 0
    // clearedFederalCount: count only what's visible in the UI (same logic as selected)
    // This matches exactly what the "Federal X selections" label shows
    const clearedFederalCount = numberOfFederalSelections

    // Track RiaSourcesClearAllClicked event
    trackEvent(AnalyticsEvent.RiaSourcesClearAllClicked, {
      riaClearedStatesCount: clearedStatesCount,
      riaClearedFederalCount: clearedFederalCount,
      conversationId,
    })

    // Clear research filters state (listener middleware will auto-sync to requestParams)
    dispatch(researchFiltersActions.clearAllSources({ conversationId }))

    // Clear date range
    dispatch(
      AgentConversationFormActions.setCaselawDateRange({
        conversationId,
        dateLow: null,
        dateHigh: null,
      })
    )

    // Clear include unpublished cases
    dispatch(AgentConversationFormActions.setIncludeUnpublishedCases({ conversationId, includeUnpublishedCases: false }))

    // Clear warning banner
    setShowWarningBanner(false)
  }, [conversationId, dispatch, trackEvent, researchFilters, numberOfFederalSelections, setShowWarningBanner])

  // Handle apply button click - closes the dialog and tracks delta
  const handleApplyButtonClick = useCallback(() => {
    const statesCount = researchFilters?.selectedStateNames?.length ?? 0
    const federalCount = numberOfFederalSelections

    // Calculate deltas based on initial state
    let federalAdded = 0
    let federalRemoved = 0

    if (initialStateRef.current) {
      // Calculate federal changes
      const initialFederalCount = initialStateRef.current.federalCount
      const deltaFederal = federalCount - initialFederalCount

      if (deltaFederal > 0) {
        federalAdded = deltaFederal
      } else if (deltaFederal < 0) {
        federalRemoved = Math.abs(deltaFederal)
      }
    }

    // Track RiaSourcesApplyClicked event with delta information
    trackEvent(AnalyticsEvent.RiaSourcesApplyClicked, {
      riaSelectedStatesCount: statesCount,
      riaSelectedFederalCount: federalCount,
      riaAllFederalSourcesSelected,
      riaIncludeFederalContent: includeRelatedFederalContent,
      riaIncludeUnpublishedCaseLaw: includeUnpublishedCases,
      riaDateRange: formatRiaDateRange(dateRange),
      riaFederalAdded: federalAdded,
      riaFederalRemoved: federalRemoved,
      conversationId,
    })

    // Reset initial state ref
    initialStateRef.current = null

    // No sync needed - listener middleware already synced on every change
    // Just close the dialog
    onOpenChange(false)
  }, [
    onOpenChange,
    trackEvent,
    researchFilters,
    numberOfFederalSelections,
    includeRelatedFederalContent,
    includeUnpublishedCases,
    dateRange,
    conversationId,
    riaAllFederalSourcesSelected,
  ])

  return {
    isResearchEnabled,
    activeTab,
    dateRange,
    showWarningBanner,
    stateSelectionProps: {
      selectedStates,
      handleStateToggle,
      setShowWarningBanner,
      includeUnpublishedCases,
      includeRelatedFederalContent,
    },
    handleTabChange,
    handleClearButtonClick,
    handleApplyButtonClick,
    handleDateRangeChange,
  }
}
